import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Layers, ShieldCheck, Sparkles } from "lucide-react";

const STATS = [
  { icon: Layers, value: 70, suffix: "+", label: "Projects Delivered", pct: 92, testid: "hero-stat-wraps-completed" },
  { icon: ShieldCheck, value: 5, suffix: "YR", label: "Film Warranty", pct: 100, testid: "hero-stat-warranty-years", accent: true },
  { icon: Sparkles, value: 100, suffix: "%", label: "Bubble-Free Finish", pct: 100, testid: "hero-stat-rating" },
];

const CountUp = ({ to, suffix, start }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / 1400, 1);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, to]);
  return <>{n}{suffix}</>;
};

const Stat = ({ s, i, start }) => {
  const Icon = s.icon;
  return (
    <motion.div
      data-testid={s.testid}
      initial={{ opacity: 0, y: 14 }}
      animate={start ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 1.15 + i * 0.12 }}
      className="group relative flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-5 py-4 min-w-[210px] hover:border-[#FF6B00]/50 transition-colors duration-300"
    >
      <span className="relative shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF2A2A] to-[#FF6B00] flex items-center justify-center shadow-[0_8px_24px_rgba(255,80,0,0.35)]">
        <Icon size={20} className="text-white" />
      </span>
      <div className="flex-1">
        <div className={`font-display text-3xl font-extrabold leading-none ${s.accent ? "text-[#FF6B00]" : "text-white"}`}>
          <CountUp to={s.value} suffix={s.suffix} start={start} />
        </div>
        <div className="font-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1.5">{s.label}</div>
        <div className="mt-2 h-[3px] w-full rounded-full bg-white/10 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={start ? { width: `${s.pct}%` } : {}}
            transition={{ duration: 1.2, delay: 1.3 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-full bg-gradient-to-r from-[#FF2A2A] to-[#FF6B00]"
          />
        </div>
      </div>
    </motion.div>
  );
};

export const HeroStats = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  return (
    <div ref={ref} data-testid="hero-stats-infographic" className="mt-14 flex flex-wrap gap-4 border-t border-white/10 pt-8">
      {STATS.map((s, i) => <Stat key={s.testid} s={s} i={i} start={inView} />)}
    </div>
  );
};
